import { Injectable, inject } from '@angular/core';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { select, Store } from '@ngrx/store';
import {
  Subject,
  takeUntil,
  debounce,
  timer,
  fromEvent,
  take,
  switchMap,
  startWith,
  filter,
  catchError,
  of,
  distinctUntilChanged,
  tap,
  map,
} from 'rxjs';
import { SalesPlanSubscriptionBlockViewUtilsService } from './sales-plan-subscription-blockview-utils.service';
import { PlanBlockSeatExtendedDto, SeatPriceApplication, SeatSelection } from '../models/sales-plan.model';
import Konva from 'konva';
import {
  OrderStatusEnumResponseDto,
  PlanBlockSeatResponseDto,
  SeatsBlockGroupResponseDto,
} from '@core/services/apiclient';
import { OrderSeatsIds, SynchronizeSelectionUpdateTimeout } from '../models/sales-plan-constants';
import { SalesPlanBlockViewNotificationService } from './sales-plan-blockview-notification.service';
import { SeatStatusPayloadNotification } from '../models/notification.model';
import { LoggerService } from '@core/services/logger.service';
import { SalesEventTypeEnum } from '@sales/models/sales.model';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanSubscriptionBlockViewCommunicationService {
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly utils = inject(SalesPlanSubscriptionBlockViewUtilsService);
  private readonly notification = inject(SalesPlanBlockViewNotificationService);
  private readonly loggerService = inject(LoggerService);

  private destroy$ = new Subject<void>();
  private requestCacheRefresh$ = new Subject<string[]>();
  private selection: SeatSelection[] = [];
  seatLayer: Konva.Layer;
  private isResubPlan = false;
  currentSubscriptionId: number;

  public init(seatLayer: Konva.Layer, isResubPlan: boolean) {
    if (this.destroy$.closed) {
      this.destroy$ = new Subject<void>();
    }
    this.seatLayer = seatLayer;
    this.isResubPlan = isResubPlan;
    this.initPlanOfSubscriptionNotification();
    this.getSeatSearchResult(seatLayer);
    setTimeout(() => {
      this.getCurrentUserRemoteSeatSelection();
    }, 500);
  }

  public clearSelection() {
    this.selection = [];
  }

  /**
   * when the current user select a seat, dispatches an action to the store, calls the api to flag the seats
   * when the api responds with 200, the store is updated and this service receives the updated current selection
   */
  public getCurrentUserRemoteSeatSelection() {
    if (this.isResubPlan) {
      this.store
        .pipe(
          takeUntil(this.destroy$),
          select(fromSalesPlan.selectors.SelectResubSeatSelection),
          map((selection) => (selection ? [selection] : [])),
          distinctUntilChanged(
            (previous, current) =>
              previous?.[0]?.id === current?.[0]?.id && previous?.length === current?.length,
          ),
        )
        .subscribe({
          next: (value) => {
            this.synchronizeWithRemoteEventSeatSelection(value);
          },
        });
      return;
    }

    const selection$ = this.store.pipe(select(fromSalesPlan.selectors.selectSeatSelection), take(1));
    const synchronizationTimeout = SynchronizeSelectionUpdateTimeout;
    const clickTarget = this.seatLayer?.getStage()?.container() ?? document;
    // check when the user stops clicking for seat selection, wait for 2 seconds and call the store to see if local and remote selections
    // are desynchronized
    const mouseClickIdle$ = fromEvent(clickTarget, 'click').pipe(
      startWith({ ignoreDebounce: true }),
      debounce((event) =>
        timer((event as { ignoreDebounce?: boolean })?.ignoreDebounce ? 0 : synchronizationTimeout),
      ),
    );
    mouseClickIdle$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => selection$),
      )
      .subscribe({
        next: (value: PlanBlockSeatExtendedDto | SeatPriceApplication[]) => {
          const data = Array.isArray(value) ? value : [value];
          this.synchronizeWithRemoteEventSeatSelection(data);
        },
      });
  }

  /**
   * when other users THAT ARE CONNECTED TO THE SAME PLAN select a seat, signalR broadcasts a notification to
   * the current user ONLY IF he's on the same plan as the concerned notification
   */
  public initPlanOfSubscriptionNotification() {
    const detailOfSubscription$ = this.store.pipe(
      select(fromSalesPlan.selectors.selectSubscriptionDetails),
      filter((id) => id !== null && id !== undefined),
      distinctUntilChanged(),
      tap((value) => (this.currentSubscriptionId = value.id)),
    );

    detailOfSubscription$
      .pipe(
        switchMap((detailOfSubscription) => {
          this.loggerService.info('T&L - Subscription ID received:', detailOfSubscription.id);
          return this.notification.initForSubscription(detailOfSubscription.id).pipe(
            catchError((err) => {
              this.loggerService.error(
                'T&L - Join room failed for session ID:',
                detailOfSubscription.id,
                err,
              );
              return of(void 0);
            }),
          );
        }),
        switchMap(() => this.notification.seatStatuses$()),
        filter((notifications) => Array.isArray(notifications) && notifications.length > 0),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notifications) => {
          const parentGroupIds = this.processNotifications(notifications);
          this.requestCacheRefresh(parentGroupIds);
        },
        error: (err) => {
          this.loggerService.error('T&L - SignalR broadcast notification failed:', err);
        },
      });
  }

  public getSeatSearchResult(selectionLayer: Konva.Layer) {
    this.store
      .pipe(takeUntil(this.destroy$), select(fromSalesPlan.selectors.selectSeatSearchResult))
      .subscribe({
        next: (result: SeatsBlockGroupResponseDto[]) =>
          this.utils.drawSearchResultIcons(result, selectionLayer),
      });
  }

  /**
   * calls the selection sequence: store -> effect -> api to flag the seats
   * and select the same seats locally without waiting for the api response
   */
  public updateSeatSelection(selection: SeatSelection[]) {
    if (this.isSelectionBlocked(selection)) {
      return;
    }
    this.applySelectionToPlan(selection);
    this.store.dispatch(
      fromSalesPlan.actions.updateSeatSelection({
        selection: selection.filter((value) => !!value).map(({ data }) => data),
      }),
    );
  }

  public updateResubSeatSelection(selection: SeatSelection) {
    if (selection.data.status !== OrderSeatsIds[OrderStatusEnumResponseDto.Free]) {
      return;
    }

    if (selection?.data?.id === this.selection[0]?.data?.id) {
      return;
    } else {
      this.applySelectionToPlan(selection);
      this.store.dispatch(
        fromSalesPlan.actions.updateResubSeatSelection({
          selection: selection.data,
        }),
      );
    }
  }

  /**
   * called when selection/unselecting outside the plan. See sales-plan-subscription-basket
   */
  public updateSeatSelectionOutsidePlan(seats: PlanBlockSeatResponseDto[]) {
    if (this.isUnnumberedSeatSelectioned(seats)) {
      this.store.dispatch(
        fromSalesPlan.actions.updateSeatSelection({
          selection: seats,
        }),
      );
    } else {
      this.updateSeatSelection(this.convertToSeatSelection(seats));
    }
  }

  private isUnnumberedSeatSelectioned(seats: any) {
    return seats?.every((seat: { type: string }) => seat.type === 'unnumbered');
  }

  /**
   * if the new selection is included in the old selection array, then we must unselect the seats
   * else we add it to the old selection
   * finally, we request the cache to refresh on the affected parent groups
   */
  private applySelectionToPlan(newSelection: SeatSelection | SeatSelection[]) {
    const newSelections = Array.isArray(newSelection) ? newSelection : [newSelection];

    // the newSelection elements are being deflagged
    if (this.isUnselection(newSelections)) {
      this.removeUnselectedSeats(newSelections);
      this.utils.restoreSelectedNodes(newSelections, this.seatLayer);
    } else {
      this.addNewSelectedSeats(newSelections);
      this.utils.highlightSelectedNodes(newSelections, this.seatLayer);
    }
    this.requestCacheRefreshForSelection(newSelections);
  }

  /**
   * this function is used to synchronize local & remote selections, if we have a delta between the two
   * we must synchonize the local selection to the remote one.
   * usecase: errors, if a seat is selected locally and the api responds with KO, we must reset this selection
   */
  private synchronizeWithRemoteEventSeatSelection(remoteSelection: SeatPriceApplication[]) {
    const _remoteSelection = this.convertToSeatSelection(remoteSelection);
    const seatsToRemove = this.selection.filter(
      ({ seatId }) => !_remoteSelection.some((seat) => seat?.seatId === seatId),
    );
    const seatsToAdd = _remoteSelection.filter(
      ({ seatId }) => !this.selection.some((seat) => seat?.seatId === seatId),
    );
    if (seatsToRemove?.length) {
      this.removeUnselectedSeats(seatsToRemove);
      this.utils.restoreSelectedNodes(seatsToRemove, this.seatLayer);
    }

    if (seatsToAdd?.length) {
      this.addNewSelectedSeats(seatsToAdd);
      this.utils.highlightSelectedNodes(seatsToAdd, this.seatLayer);
    }

    if (seatsToRemove?.length || seatsToAdd?.length) {
      this.requestCacheRefreshForSelection([...seatsToAdd, ...seatsToRemove]);
    }
  }

  /**
   * checks if selection or unselection:
   * unselection if every targeted element is included in the selection array
   * selection if atleast one element isn't in the selection array
   */
  private isUnselection(newSelection: SeatSelection[]) {
    return (
      !!newSelection?.length &&
      newSelection?.length <= this.selection?.length &&
      newSelection.every((selection) => this.selection.some(({ seatId }) => selection?.seatId === seatId))
    );
  }

  private isSelectionBlocked(selection: SeatSelection[]) {
    return selection?.every((seat) => seat.data.status !== OrderSeatsIds[OrderStatusEnumResponseDto.Free]);
  }

  private processNotifications(notifications: SeatStatusPayloadNotification[]) {
    const parentGroupIds: string[] = [];
    notifications.forEach((notification) => {
      const node = this.utils.getSeatById(notification?.seatId);
      const notifiedSeat = this.utils.data.seats?.find((seat) => seat.id === notification.seatId);

      if (!notifiedSeat) {
        return;
      }

      try {
        notifiedSeat.isAvailable = !notification.isBooked;
      } catch {
        this.utils.data.seats = this.utils.data.seats.map((seat) =>
          seat.id === notification.seatId ? { ...seat, seatIsAvailable: !notification.isBooked } : seat,
        );
      }

      if (node) {
        const seatBox = node.findOne(`#sales-seat-box-${notification.seatId}`);
        seatBox.setAttrs({
          ...this.utils.updateSeatState(notifiedSeat),
        });
        if (!parentGroupIds.includes(node?.name())) {
          parentGroupIds.push(node?.name());
        }
      }
    });

    return parentGroupIds;
  }

  private addNewSelectedSeats(newSelection: SeatSelection[]) {
    this.selection = [...this.selection, ...newSelection]
      .filter(({ seatId }, index, self) => self.findIndex((seat) => seat?.seatId === seatId) === index)
      .map((sel) => {
        const selectedSeat = this.selection.find((elt) => elt?.seatId === sel?.seatId);
        if (selectedSeat?.node) {
          return {
            ...selectedSeat,
            seatId: sel?.seatId,
          };
        }
        const seatNode: Konva.Group = this.utils.getSeatById(sel?.seatId);
        if (seatNode) {
          const seatBox = seatNode?.findOne(`#sales-seat-box-${sel?.seatId}`);
          if (seatBox) {
            seatBox.setAttrs({
              ...this.utils.updateSeatState(sel?.data, true),
            });
          }
        }
        return {
          data: sel?.data,
          seatId: sel?.seatId,
          node: seatNode,
        };
      });
  }

  private removeUnselectedSeats(newSelection: SeatSelection[]) {
    this.selection = this.selection.filter(
      ({ seatId }) => !newSelection.some((seat) => seat?.seatId === seatId),
    );
  }

  private convertToSeatSelection(seats: PlanBlockSeatResponseDto[]): SeatSelection[] {
    return (seats || []).map((value) => ({
      data: value,
      node: this.utils.getSeatById(value?.id),
      seatId: value?.id,
    }));
  }

  private requestCacheRefreshForSelection(selection: SeatSelection[]) {
    const parentGroupIds = selection
      .map(({ node }) => node?.name())
      .filter((groupId, index, self) => self.indexOf(groupId) === index);
    this.requestCacheRefresh(parentGroupIds);
  }

  public requestCacheRefresh(parentGroupIds: string[]) {
    this.requestCacheRefresh$.next(parentGroupIds);
  }

  public getCacheRefreshSignal() {
    return this.requestCacheRefresh$.asObservable();
  }

  public destroy() {
    this.destroy$.next();
    this.notification.leaveGroup(SalesEventTypeEnum.Subscription, this.currentSubscriptionId);
  }
}
